import api from '../api'
import { useTranslation } from 'react-i18next'
// import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
// eslint-disable-next-line no-restricted-imports
import { parseISO, formatISO, isSameDay, isBefore } from 'date-fns'
import { getUniqueId } from '../utils/id'
import usePassengersDisplay from './passengers-display'

// import useAnalytics from './analytics'

class Criteria {
  constructor() {
    this.airports = []
    this.carriers = []
    this.departure_hour = []
    this.direct_flight_only = false
    this.max_stopover = null
    this.max_stopover_duration = null
  }
}

const validateSlice = (slice) => slice.origin && slice.destination && slice.date

const formatSlices = ({ slices, tripType }) => {
  if (tripType === 'oneway') {
    return [
      {
        origin: slices[0].origin.value,
        destination: slices[0].destination.value,
        departure_date: formatISO(slices[0].date),
      },
    ]
  }

  if (tripType === 'roundtrip') {
    return [
      {
        origin: slices[0].origin.value,
        destination: slices[0].destination.value,
        departure_date: formatISO(slices[0].date),
      },
      {
        origin: slices[0].destination.value,
        destination: slices[0].origin.value,
        departure_date: formatISO(slices[1].date),
      },
    ]
  }

  return slices.map((slice) => ({
    origin: slice.origin.value,
    destination: slice.destination.value,
    departure_date: formatISO(slice.date),
  }))
}

const formatAirport = (airport) => ({
  value: airport.iata_code,
  label: airport.name,
  subLabel: `${airport.city_name} ${airport.country_name}`,
  selected: `${airport.city_name} (${airport.iata_code})`,
})

const offerRequestToStateValues = (offerRequest) => {
  const isRoundTrip =
    offerRequest.slices.length === 2 &&
    offerRequest.slices[0].origin.iata_code ===
      offerRequest.slices[1].destination.iata_code

  return {
    cabinClass: offerRequest.cabin_class,
    numAdults: (offerRequest.passengers || []).filter((p) => p.type === 'adult')
      .length,
    numChildren: (offerRequest.passengers || []).filter(
      (p) => p.type === 'child'
    ).length,
    numBabies: (offerRequest.passengers || []).filter(
      (p) => p.type === 'infant'
    ).length,
    tripType:
      offerRequest.type === 'multi'
        ? 'multi'
        : isRoundTrip
        ? 'roundtrip'
        : 'oneway',
    slices: offerRequest.slices.map((slice) =>
      createSlice({
        origin: formatAirport(slice.origin),
        destination: formatAirport(slice.destination),
        date: new Date(parseISO(slice.departure_date)),
      })
    ),
    partnership: offerRequest.partnership,
  }
}

const isEqual = (a, b) =>
  a instanceof Date
    ? isSameDay(a, b)
    : typeof a === 'object' && a !== null
    ? Object.keys(a)
        .filter((key) => key !== 'id')
        .every((key) => isEqual(a?.[key], b?.[key]))
    : a === b

const isDirty = (offerRequest, state) => {
  if (offerRequest) {
    const offerRequestValues = offerRequestToStateValues(offerRequest)
    return Object.keys(state).some((key) =>
      Array.isArray(state[key])
        ? state[key].some(
            (value, index) => !isEqual(value, offerRequestValues[key][index])
          )
        : !isEqual(state[key], offerRequestValues[key])
    )
  }

  return Object.keys(state).some((key) =>
    Array.isArray(state[key]) ? state[key] > 0 : !!state[key]
  )
}

const createSlice = (slice) => ({
  ...slice,
  id: getUniqueId('search'),
})

const useSearch = (offerRequest, defaultTripType) => {
  // const { track } = useAnalytics()
  // const router = useRouter()
  const [tripType, setTripType] = useState(defaultTripType || 'roundtrip')
  const [slices, setSlices] = useState(() => [createSlice(), createSlice()])
  const [numAdults, setNumAdults] = useState(1)
  const [numChildren, setNumChildren] = useState(0)
  const [numBabies, setNumBabies] = useState(0)
  const [cabinClass, setCabinClass] = useState('economy')
  const [offers, setOffers] = useState([])
  const [bestOffers, setBestOffers] = useState(null)
  const [partnership, setPartnership] = useState(null)

  const { t } = useTranslation()

  useEffect(() => {
    if (offerRequest) {
      const newState = offerRequestToStateValues(offerRequest)

      setCabinClass(newState.cabinClass)
      setNumAdults(newState.numAdults)
      setNumChildren(newState.numChildren)
      setNumBabies(newState.numBabies)
      setSlices(newState.slices)
      setTripType(newState.tripType)
      setOffers([])
      setBestOffers(null)
      setPartnership(newState.partnership)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerRequest?.id])

  const isValid = !!(
    (tripType === 'oneway'
      ? validateSlice(slices[0])
      : tripType === 'roundtrip'
      ? validateSlice(slices[0]) && slices[1]?.date
      : slices.every((slice) => validateSlice(slice))) &&
    (numAdults || numChildren || numBabies) &&
    cabinClass
  )

  const getErrors = ({ tripType, slices }) => {
    return {
      slices: slices
        ? slices.map((S) => {
            let e = { id: S.id }
            if (!S.origin) e.origin = t('defaults.forms.required')
            if (!S.destination) {
              e.destination = t('defaults.forms.required')
            }
            if (!S.date) e.date = t('defaults.forms.required')
            return e
          })
        : [],
      missingReturnDate:
        tripType === 'roundtrip' &&
        slices[0].date &&
        !slices[1]?.date &&
        t('hooks.search.errors.missingReturnDate'),
    }
  }

  const passengersDisplay = usePassengersDisplay({
    numAdults,
    numChildren,
    numBabies,
  })

  return {
    id: offerRequest?.id,
    isDirty: isDirty(offerRequest, {
      cabinClass,
      numAdults,
      numChildren,
      numBabies,
      slices,
      tripType,
    }),
    isValid,
    errors: getErrors({ tripType, slices }),
    tripType,
    setTripType,
    slices,
    addSlice: ({ origin, destination }) => {
      setSlices((prevSlices) => [
        ...prevSlices,
        createSlice({ origin, destination }),
      ])
    },
    removeSlice: (sliceId) => {
      setSlices((prevSlices) => {
        const newSlices = [...prevSlices]
        const sliceIndex = newSlices.findIndex((slice) => slice.id === sliceId)
        newSlices.splice(sliceIndex, 1)
        return newSlices
      })
    },
    setOrigin: (newOrigin, sliceId) => {
      setSlices((prevSlices) => {
        const newSlices = [...prevSlices]
        const sliceIndex = newSlices.findIndex((slice) => slice.id === sliceId)

        if (sliceIndex >= 0) {
          newSlices.splice(sliceIndex, 1, {
            ...prevSlices[sliceIndex],
            origin: newOrigin,
          })
        } else {
          newSlices.push(createSlice({ origin: newOrigin }))
        }

        return newSlices
      })
    },
    setDestination: (newDestination, sliceId) => {
      setSlices((prevSlices) => {
        const newSlices = [...prevSlices]
        const sliceIndex = newSlices.findIndex((slice) => slice.id === sliceId)

        if (sliceIndex >= 0) {
          newSlices.splice(sliceIndex, 1, {
            ...prevSlices[sliceIndex],
            destination: newDestination,
          })
        } else {
          newSlices.push(createSlice({ destination: newDestination }))
        }

        return newSlices
      })
    },
    setDates: (newDates, id) => {
      setSlices((prevSlices) => {
        const newSlices = [...prevSlices]

        const updateDate = (newDate, sliceId) => {
          const sliceIndex = newSlices.findIndex(
            (slice) => slice.id === sliceId
          )

          if (sliceIndex >= 0) {
            newSlices.splice(sliceIndex, 1, {
              ...prevSlices[sliceIndex],
              date: newDate,
            })

            const datesToRemoveIndexes = newSlices.reduce(
              (indexes, slice, index) => {
                if (
                  index > sliceIndex &&
                  slice.date &&
                  isBefore(slice.date, newDate)
                ) {
                  return [...indexes, index]
                }

                return indexes
              },
              []
            )
            datesToRemoveIndexes.forEach((index) => {
              newSlices.splice(index, 1, {
                ...prevSlices[index],
                date: undefined,
              })
            })
          } else {
            newSlices.push(createSlice({ date: newDate }))
          }
        }

        if (tripType === 'roundtrip') {
          updateDate(newDates[0], prevSlices[0].id)
          updateDate(newDates[1], prevSlices[1].id)
        } else {
          updateDate(newDates[0], id)
        }

        return newSlices
      })
    },
    getMinDate: (sliceId) => {
      const index = slices.findIndex((slice) => slice.id === sliceId)
      return tripType === 'multi' && index > 0
        ? slices
            .slice(0, index)
            .reduce((minDate, slice) => slice.date || minDate, undefined)
        : undefined
    },
    getDates: (sliceId) =>
      tripType === 'roundtrip'
        ? [slices[0]?.date, slices[1]?.date].filter(Boolean)
        : [slices.find((slice) => slice.id === sliceId)?.date].filter(Boolean),
    numAdults,
    setNumAdults,
    numChildren,
    setNumChildren,
    numBabies,
    setNumBabies,
    cabinClass,
    setCabinClass,
    offers,
    setOffers,
    bestOffers,
    setBestOffers,
    partnership,
    totalPassengerCount: numAdults + numChildren + numBabies,
    shortPassengersCabinClassDisplay: `${passengersDisplay}, ${t(
      `hooks.search.cabinClass.${cabinClass}`
    )}`,
    longPassengersCabinClassDisplay: `${passengersDisplay}, ${t(
      `hooks.search.longCabinClass.${cabinClass}`
    )}`,
    createSearch: ({ partner }) => {
      if (isValid) {
        const criteria = new Criteria()

        const formattedSearch = {
          available_partnerships: [
            {
              name: partner,
              visited_at: Math.floor(new Date().getTime() / 1000),
            },
          ],
          type: tripType === 'multi' ? 'multi' : 'bundle',
          slices: formatSlices({ slices, tripType }),
          passengers: [
            ...Array(numAdults).fill({ type: 'adult' }),
            ...Array(numChildren).fill({ type: 'child' }),
            ...Array(numBabies).fill({ type: 'infant' }),
          ],
          cabin_class: cabinClass,
          criteria,
        }

        // track('prebooking_flight_searched', {
        //   flight_inbound_airport: formattedSearch.slices[0].origin,
        //   flight_outbound_airport:
        //     tripType === 'roundtrip'
        //       ? formattedSearch.slices[0].destination
        //       : formattedSearch.slices[formattedSearch.slices.length - 1]
        //           .destination,
        //   flight_outbound_date: format(
        //     new Date(formattedSearch.slices[0].departure_date),
        //     'yyyy-MM-dd'
        //   ),
        //   flight_inbound_date:
        //     formattedSearch.slices.length > 1
        //       ? format(
        //           new Date(
        //             formattedSearch.slices[
        //               formattedSearch.slices.length - 1
        //             ].departure_date
        //           ),
        //           'yyyy-MM-dd'
        //         )
        //       : null,
        //   flight_nb_of_passengers: numAdults + numChildren + numBabies,
        //   flight_type: tripType,
        // })

        return api.search.createSearch(formattedSearch)
      }
    },
  }
}

export default useSearch
